import { useState, useEffect } from 'react';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader } from '../../components/ui';
import { LuBeer, LuFileText, LuGift, LuPackage, LuPencil, LuShoppingBag, LuStore, LuTrash2, LuUtensils, LuWallet, LuWine, LuX } from 'react-icons/lu';

import { fmt, fmtF } from '../../theme';
export default function Kits() {
  const [kits, setKits] = useState([]);
  const [etablissements, setEtablissements] = useState([]);
  const [produits, setProduits] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [vue, setVue] = useState('liste');
  const [kitSelectionne, setKitSelectionne] = useState(null);
  const [message, setMessage] = useState('');
  const [recherche, setRecherche] = useState('');
  const [filtreEtab, setFiltreEtab] = useState('tous');
  const [page, setPage] = useState(1);
  const parPage = 8;

  const [form, setForm] = useState({
    nom: '',
    description: '',
    prix_kit: '',
    etablissement_id: '',
    produits: [] // [{ produit_id, quantite }]
  });

  useEffect(() => { chargerDonnees(); }, []);

  useEffect(() => {
    if (form.etablissement_id) {
      chargerProduits(form.etablissement_id);
    } else {
      setProduits([]);
    }
  }, [form.etablissement_id]);

  const chargerDonnees = async () => {
    try {
      const [kitsRes, etabRes] = await Promise.all([
        API.get('/kits'),
        API.get('/etablissements')
      ]);
      setKits(kitsRes.data.kits || []);
      setEtablissements(etabRes.data.etablissements || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const chargerProduits = async (etabId) => {
    try {
      const res = await API.get('/produits?etablissement_id=' + etabId);
      setProduits((res.data.produits || []).filter(p => p.etablissement_id === parseInt(etabId)));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.produits.length === 0) {
      setMessage('❌ Ajoutez au moins un produit au kit');
      return;
    }
    try {
      if (kitSelectionne) {
        await API.put('/kits/' + kitSelectionne.id, form);
        setMessage('✅ Kit modifié avec succès !');
      } else {
        await API.post('/kits', form);
        setMessage('✅ Kit créé avec succès !');
      }
      chargerDonnees();
      setVue('liste');
      setKitSelectionne(null);
      resetForm();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleModifier = async (kit) => {
    setKitSelectionne(kit);
    await chargerProduits(kit.etablissement_id);
    setForm({
      nom: kit.nom || '',
      description: kit.description || '',
      prix_kit: kit.prix_kit || '',
      etablissement_id: kit.etablissement_id || '',
      produits: (kit.produits || []).map(p => ({
        produit_id: p.produit_id,
        quantite: p.quantite
      }))
    });
    setVue('formulaire');
  };

  const handleDesactiver = async (id) => {
    if (!window.confirm('Désactiver ce kit ?')) return;
    try {
      await API.delete('/kits/' + id);
      setMessage('✅ Kit désactivé !');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const ajouterProduit = (produitId) => {
    const existe = form.produits.find(p => p.produit_id === parseInt(produitId));
    if (existe) return;
    setForm({
      ...form,
      produits: [...form.produits, { produit_id: parseInt(produitId), quantite: 1 }]
    });
  };

  const modifierQuantite = (produitId, quantite) => {
    setForm({
      ...form,
      produits: form.produits.map(p =>
        p.produit_id === produitId ? { ...p, quantite: parseInt(quantite) || 1 } : p
      )
    });
  };

  const retirerProduit = (produitId) => {
    setForm({
      ...form,
      produits: form.produits.filter(p => p.produit_id !== produitId)
    });
  };

  // Calcul prix automatique
  const prixCalcule = form.produits.reduce((total, item) => {
    const produit = produits.find(p => p.id === item.produit_id);
    return total + (produit ? parseFloat(produit.prix) * item.quantite : 0);
  }, 0);

  const resetForm = () => {
    setForm({ nom: '', description: '', prix_kit: '', etablissement_id: '', produits: [] });
    setProduits([]);
  };

  const kitsFiltres = kits.filter(k => {
    const matchRecherche = k.nom.toLowerCase().includes(recherche.toLowerCase());
    const matchEtab = filtreEtab === 'tous' || k.etablissement_id === parseInt(filtreEtab);
    return matchRecherche && matchEtab;
  });

  const totalPages = Math.ceil(kitsFiltres.length / parPage);
  const kitsPage = kitsFiltres.slice((page - 1) * parPage, page * parPage);

  const typeIcons = { bar: <LuBeer aria-hidden />, boutique: <LuShoppingBag aria-hidden />, cave: <LuWine aria-hidden />, maquis: <LuUtensils aria-hidden /> };

  if (chargement) return <Loader />;

  return (
    <AppLayout role="admin">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Kits</h1>
            <p style={styles.headerSousTitre}>
              {kits.length + ' kit(s) créé(s)'}
            </p>
          </div>
          {vue === 'liste' ? (
            <button
              onClick={() => { resetForm(); setKitSelectionne(null); setVue('formulaire'); }}
              style={styles.boutonAjouter}
            >
              + Nouveau Kit
            </button>
          ) : (
            <button onClick={() => setVue('liste')} style={styles.boutonRetour}>
              ← Retour à la liste
            </button>
          )}
        </div>

        {/* ── LISTE ── */}
        {vue === 'liste' && (
          <>
            <div style={styles.filtres}>
              <input
                type="text"
                placeholder="Rechercher un kit..."
                value={recherche}
                onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
                style={styles.rechercheInput}
              />
              <select
                value={filtreEtab}
                onChange={(e) => { setFiltreEtab(e.target.value); setPage(1); }}
                style={styles.filtreSelect}
              >
                <option value="tous">Tous les établissements</option>
                {etablissements.map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>
            </div>

            <div style={styles.grille}>
              {kitsPage.map((kit, i) => (
                <div key={i} style={styles.kitCard}>
                  <div style={styles.kitCardHeader}>
                    <span style={{ fontSize: 32 }}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /></span>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20,
                      fontSize: 11, fontWeight: 700,
                      background: kit.actif ? '#ECFDF5' : '#FEF2F2',
                      color: kit.actif ? '#059669' : '#DC2626'
                    }}>
                      {kit.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </div>

                  <div style={styles.kitNom}>{kit.nom}</div>
                  <div style={styles.kitEtab}>
                    {typeIcons[kit.etablissement_type] || <LuStore aria-hidden />} {kit.etablissement_nom}
                  </div>

                  {kit.description && (
                    <div style={styles.kitDesc}>{kit.description}</div>
                  )}

                  {/* PRODUITS DU KIT */}
                  <div style={styles.kitProduits}>
                    {(kit.produits || []).slice(0, 3).map((p, j) => (
                      <div key={j} style={styles.kitProduitItem}>
                        <span style={{ fontSize: 11 }}>{p.produit_nom || p.nom}</span>
                        <span style={styles.kitProduitQte}>{'×' + (p.quantite ?? p.qte_kit ?? 1)}</span>
                      </div>
                    ))}
                    {(kit.produits || []).length > 3 && (
                      <div style={{ color: '#64748B', fontSize: 11 }}>
                        {'+' + ((kit.produits || []).length - 3) + ' autres produits'}
                      </div>
                    )}
                  </div>

                  <div style={styles.kitPrix}>
                    {fmtF(parseInt(kit.prix_kit))}
                  </div>

                  <div style={styles.kitActions}>
                    <button onClick={() => handleModifier(kit)} style={styles.btnModifier}>
                      <LuPencil aria-hidden style={{ verticalAlign: '-0.125em' }} /> Modifier
                    </button>
                    <button onClick={() => handleDesactiver(kit.id)} style={styles.btnDesactiver} aria-label="Désactiver" title="Désactiver">
                      <LuTrash2 aria-hidden style={{ verticalAlign: '-0.125em' }} />
                    </button>
                  </div>
                </div>
              ))}

              {kitsFiltres.length === 0 && (
                <div style={styles.vide}>
                  <div style={{ fontSize: 48 }}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Aucun kit trouvé</p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ ...styles.pagBtn, opacity: page === 1 ? 0.4 : 1 }}>←</button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)} style={{
                    ...styles.pagNum,
                    background: page === i + 1 ? 'var(--accent)' : 'white',
                    color: page === i + 1 ? 'white' : '#475569'
                  }}>{i + 1}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{ ...styles.pagBtn, opacity: page === totalPages ? 0.4 : 1 }}>→</button>
              </div>
            )}
          </>
        )}

        {/* ── FORMULAIRE ── */}
        {vue === 'formulaire' && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitre}>
              {kitSelectionne ? 'Modifier le kit' : 'Nouveau Kit'}
            </h2>

            <form onSubmit={handleSubmit}>

              {/* SECTION 1 — INFOS */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /> Informations du Kit</div>
                <div style={styles.formGrid}>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}>Nom du kit *</label>
                    <input
                      type="text"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Kit Anniversaire, Menu Complet..."
                      required
                    />
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Établissement *</label>
                    <select
                      value={form.etablissement_id}
                      onChange={(e) => setForm({ ...form, etablissement_id: e.target.value, produits: [] })}
                      style={styles.select}
                      required
                    >
                      <option value="">Sélectionnez un établissement</option>
                      {etablissements.map(e => (
                        <option key={e.id} value={e.id}>
                          {e.nom}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuWallet aria-hidden style={{ verticalAlign: '-0.125em' }} /> Prix du kit (FCFA) *</label>
                    <input
                      type="number" min="0"
                      value={form.prix_kit}
                      onChange={(e) => setForm({ ...form, prix_kit: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: 5000"
                      required
                    />
                    {prixCalcule > 0 && (
                      <div style={styles.prixCalculeInfo}>
                        {'Prix total des produits : ' + fmtF(prixCalcule)}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, prix_kit: prixCalcule.toString() })}
                          style={styles.btnUtiliserPrix}
                        >
                          Utiliser ce prix
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuFileText aria-hidden style={{ verticalAlign: '-0.125em' }} /> Description</label>
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Kit pour 2 personnes..."
                    />
                  </div>

                </div>
              </div>

              {/* SECTION 2 — PRODUITS DU KIT */}
              {form.etablissement_id && (
                <div style={styles.sectionForm}>
                  <div style={styles.sectionFormTitre}>
                    <LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Composition du Kit
                    <span style={styles.compteurBadge}>
                      {form.produits.length + ' produit(s)'}
                    </span>
                  </div>

                  {/* AJOUTER UN PRODUIT */}
                  <div style={styles.ajouterProduitBox}>
                    <select
                      onChange={(e) => { if (e.target.value) ajouterProduit(e.target.value); e.target.value = ''; }}
                      style={styles.select}
                      defaultValue=""
                    >
                      <option value="">Sélectionnez un produit à ajouter...</option>
                      {produits
                        .filter(p => !form.produits.find(fp => fp.produit_id === p.id))
                        .map(p => (
                          <option key={p.id} value={p.id}>
                            {p.nom + ' — ' + fmt(parseInt(p.prix)) + ' FCFA (stock: ' + p.quantite + ')'}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  {/* LISTE DES PRODUITS AJOUTÉS */}
                  {form.produits.length === 0 ? (
                    <div style={{ color: '#64748B', fontSize: 13, textAlign: 'center', padding: 20 }}>
                      Aucun produit ajouté. Sélectionnez des produits ci-dessus.
                    </div>
                  ) : (
                    <div style={styles.produitsList}>
                      {form.produits.map((item, i) => {
                        const produit = produits.find(p => p.id === item.produit_id);
                        if (!produit) return null;
                        return (
                          <div key={i} style={styles.produitItem}>
                            <div style={styles.produitItemInfo}>
                              {produit.image_principale ? (
                                <img
                                  src={SERVER_URL + '/uploads/produits/' + produit.image_principale.replace(/\\/g, '/').split('/').pop()}
                                  alt={produit.nom}
                                  style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={styles.produitItemAvatar}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                              )}
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13 }}>{produit.nom}</div>
                                <div style={{ color: '#64748B', fontSize: 11 }}>
                                  {fmt(parseInt(produit.prix)) + ' FCFA • Stock: ' + produit.quantite}
                                </div>
                              </div>
                            </div>
                            <div style={styles.produitItemActions}>
                              <div style={styles.quantiteControl}>
                                <button
                                  type="button"
                                  onClick={() => modifierQuantite(item.produit_id, Math.max(1, item.quantite - 1))}
                                  style={styles.btnQte}
                                >
                                  −
                                </button>
                                <span style={styles.quantiteVal}>{item.quantite}</span>
                                <button
                                  type="button"
                                  onClick={() => modifierQuantite(item.produit_id, item.quantite + 1)}
                                  style={styles.btnQte}
                                >
                                  +
                                </button>
                              </div>
                              <span style={styles.sousTotal}>
                                {fmtF((parseInt(produit.prix) * item.quantite))}
                              </span>
                              <button
                                type="button"
                                onClick={() => retirerProduit(item.produit_id)}
                                style={styles.btnRetirer}
                               aria-label="Retirer" title="Retirer">
                                <LuX aria-hidden style={{ verticalAlign: '-0.125em' }} />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* TOTAL */}
                      <div style={styles.totalKit}>
                        <span style={{ fontWeight: 700, color: '#0F172A' }}>
                          Total produits
                        </span>
                        <span style={{ fontWeight: 700, color: '#059669', fontSize: 16 }}>
                          {fmtF(prixCalcule)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ACTIONS */}
              <div style={styles.formActions}>
                <button type="button" onClick={() => setVue('liste')} style={styles.btnAnnuler}>
                  Annuler
                </button>
                <button type="submit" style={styles.btnSauvegarder}>
                  {kitSelectionne ? 'Sauvegarder' : 'Créer le kit'}
                </button>
              </div>

            </form>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', height: '100vh',
    fontSize: 24, color: '#475569'
  },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  filtres: { ...S.filtres, marginBottom: 16 },
  rechercheInput: { ...S.rechercheInput },
  filtreSelect: { ...S.filtreSelect, minWidth: 200 },
  grille: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16
  },
  kitCard: {
    background: 'white', borderRadius: 14,
    padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderTop: '4px solid #059669'
  },
  kitCardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 10
  },
  kitNom: { fontWeight: 700, fontSize: 16, color: '#0F172A', marginBottom: 4 },
  kitEtab: { color: '#475569', fontSize: 12, marginBottom: 8 },
  kitDesc: { color: '#64748B', fontSize: 12, marginBottom: 10, fontStyle: 'italic' },
  kitProduits: {
    background: '#F8FAFC', borderRadius: 8,
    padding: '8px 12px', marginBottom: 12
  },
  kitProduitItem: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 4
  },
  kitProduitQte: {
    background: '#EFF6FF', color: '#2563EB',
    padding: '1px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 700
  },
  kitPrix: {
    fontWeight: 700, fontSize: 20,
    color: '#059669', marginBottom: 12, textAlign: 'center'
  },
  kitActions: { display: 'flex', gap: 8 },
  btnModifier: { ...S.btnModifier, flex: 1 },
  btnDesactiver: S.btnDesactiver,
  vide: { ...S.vide, gridColumn: '1 / -1' },
  pagination: { ...S.pagination, marginTop: 16 },
  pagBtn: S.pagBtn,
  pagNum: { ...S.pagNum, width: 30 },
  formCard: S.formCard,
  formTitre: { ...S.formTitre, marginBottom: 24 },
  sectionForm: { ...S.sectionForm, marginBottom: 24 },
  sectionFormTitre: { ...S.sectionFormTitre, marginBottom: 16 },
  formGrid: S.formGrid,
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  prixCalculeInfo: {
    marginTop: 6, padding: '8px 12px',
    background: '#ECFDF5', borderRadius: 8,
    fontSize: 12, color: '#059669',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  btnUtiliserPrix: {
    background: '#059669', color: 'white',
    border: 'none', borderRadius: 6,
    padding: '3px 10px', cursor: 'pointer', fontSize: 11
  },
  compteurBadge: {
    background: '#EFF6FF', color: '#2563EB',
    padding: '2px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 700, marginLeft: 8
  },
  ajouterProduitBox: { marginBottom: 16 },
  produitsList: {
    border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden'
  },
  produitItem: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '12px 16px',
    borderBottom: '1px solid #F1F5F9', background: 'white'
  },
  produitItemInfo: { display: 'flex', alignItems: 'center', gap: 10 },
  produitItemAvatar: {
    width: 36, height: 36, borderRadius: 8,
    background: '#F8FAFC', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 18
  },
  produitItemActions: { display: 'flex', alignItems: 'center', gap: 12 },
  quantiteControl: { display: 'flex', alignItems: 'center', gap: 8 },
  btnQte: {
    width: 28, height: 28, borderRadius: '50%',
    background: '#EFF6FF', color: '#2563EB',
    border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  quantiteVal: { fontWeight: 700, fontSize: 15, minWidth: 24, textAlign: 'center' },
  sousTotal: { fontWeight: 700, color: '#2563EB', fontSize: 13, minWidth: 80, textAlign: 'right' },
  btnRetirer: {
    background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 14, color: '#DC2626'
  },
  totalKit: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '14px 16px',
    background: '#F0FFF4', borderTop: '2px solid #059669'
  },
  formActions: { ...S.formActions, marginTop: 16 },
  btnAnnuler: S.btnAnnuler,
  btnSauvegarder: S.btnSauvegarder
};